/**
 * Commercial finance calculator.
 *
 * Every figure is illustrative and derived from the static rate table in the
 * section settings. Nothing here talks to a lender.
 */
class CommercialFinanceCalculator extends HTMLElement {
  constructor() {
    super();
    this.pricePrimary = Number(this.dataset.pricePrimary) || 0;
    this.priceSecondary = Number(this.dataset.priceSecondary) || 0;
    this.rate = Number(this.dataset.rate) || 0;
    this.currency = this.dataset.currency || '£';
    this.model = 'primary';
    this.warranty4 = Number(this.dataset.warrantyFour) || 0;
    this.warranty9 = Number(this.dataset.warrantyMid) || 0;
    this.warranty10 = Number(this.dataset.warrantyPlus) || 0;

    this.beds = this.querySelector('[data-input-beds]');
    this.term = this.querySelector('[data-input-term]');
    this.towerQty = this.querySelector('[data-tower-qty]');
    this.towerBox = this.querySelector('[data-addon="per-tower"]');
    this.deposit = this.querySelector('[data-input-deposit]');
    this.revenue = this.querySelector('[data-input-revenue]');
    this.sessions = this.querySelector('[data-input-sessions]');
    // The enquiry form behind the primary CTA, when the section renders one.
    this.enquiry = this.querySelector('commercial-enquiry');
    this.out = {
      beds: this.querySelector('[data-out-beds]'),
      term: this.querySelector('[data-out-term]'),
      monthly: this.querySelector('[data-out-monthly]'),
      breakdown: this.querySelector('[data-out-breakdown]'),
      total: this.querySelector('[data-out-total]'),
      deposit: this.querySelector('[data-out-deposit]'),
      payback: this.querySelector('[data-out-payback]'),
    };
  }

  connectedCallback() {
    if (!this.beds || !this.term) return;

    this.render = this.render.bind(this);
    if (this.towerQty) {
      this.towerQty.addEventListener('input', () => { this.towerQty.dataset.touched = 'true'; });
      // Tidy the typed number once the buyer leaves the field, not while typing.
      this.towerQty.addEventListener('change', () => { this.towerQty.value = String(this.towerCount); });
    }
    // Same for the deposit: a figure above the equipment total is shown as the
    // total once the buyer moves on, since nothing is left to finance.
    if (this.deposit) {
      this.deposit.addEventListener('change', () => { this.deposit.value = String(Math.round(this.depositAmount(this.equipmentTotal))); });
    }
    this.addEventListener('input', (event) => { this.sync(event.target); this.render(); });
    this.addEventListener('change', (event) => { this.sync(event.target); this.render(); });
    this.openForViewport();
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-model]');
      if (!chip) return;
      this.model = chip.dataset.model;
      this.querySelectorAll('[data-model]').forEach((sibling) => {
        sibling.setAttribute('aria-pressed', String(sibling === chip));
      });
      this.render();
    });

    this.render();
    // Coming back to the page (Back button), redraw from what the controls
    // show, so the figures always match them.
    window.addEventListener('pageshow', () => this.render());
  }

  /*
   * The compact selects and the sliders set the same two numbers. Whichever
   * one moved, the other follows, so switching viewport never shows a stale
   * value. The sliders stay the source the maths reads from.
   */
  sync(source) {
    if (!source) return;
    const key = source.dataset.sync;
    if (key) {
      const range = key === 'beds' ? this.beds : this.term;
      if (range) range.value = source.value;
      return;
    }
    const target = source === this.beds ? 'beds' : source === this.term ? 'term' : null;
    if (!target) return;
    const select = this.querySelector(`[data-sync="${target}"]`);
    if (select) select.value = source.value;
  }

  /* Add-ons stay open on desktop and fold away on a phone, where the result
     has to sit inside the same screen as the controls. */
  openForViewport() {
    const wide = window.matchMedia('(min-width: 750px)');
    const apply = () => this.querySelectorAll('[data-open-desktop]').forEach((el) => { el.open = wide.matches; });
    apply();
    wide.addEventListener('change', apply);
  }

  get pricePerBed() {
    if (this.model === 'secondary') return this.priceSecondary;
    return this.pricePrimary;
  }

  /* 4 Reformers, then 5–9, then 10 and over. The figure is per Reformer. */
  warrantyPerBed(beds) {
    if (beds >= 10) return this.warranty10;
    if (beds >= 5) return this.warranty9;
    return this.warranty4;
  }

  syncTower() {
    if (!this.towerQty || !this.towerBox) return;
    const beds = Number(this.beds.value) || 1;
    this.towerQty.disabled = !this.towerBox.checked;
    this.towerQty.max = String(beds);
    if (!this.towerBox.checked) return;
    if (!this.towerQty.dataset.touched) this.towerQty.value = String(beds);
    else if (Number(this.towerQty.value) > beds) this.towerQty.value = String(beds);
  }

  /** Towers in the total: a whole number from 1 up to the number of Reformers. */
  get towerCount() {
    const beds = Number(this.beds.value) || 1;
    const qty = Math.floor(Number(this.towerQty && this.towerQty.value));
    if (!qty || qty < 1) return 1;
    return Math.min(qty, beds);
  }

  get addonTotal() {
    const beds = Number(this.beds.value) || 0;
    return Array.from(this.querySelectorAll('[data-addon]:checked')).reduce((sum, input) => {
      const unit = Number(input.dataset.price) || 0;
      if (input.dataset.addon === 'per-bed') return sum + unit * beds;
      if (input.dataset.addon === 'per-tower') return sum + unit * this.towerCount;
      if (input.dataset.addon === 'warranty') return sum + this.warrantyPerBed(beds) * beds;
      return sum + unit;
    }, 0);
  }

  /** A typed amount: blank, negative or not a number counts as nothing. */
  amount(input) {
    const value = Number(input && input.value);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  get equipmentTotal() {
    return (Number(this.beds.value) || 0) * this.pricePerBed + this.addonTotal;
  }

  depositAmount(equipment) {
    return Math.min(this.amount(this.deposit), equipment);
  }

  money(value) {
    return this.currency + Math.round(value).toLocaleString('en-GB');
  }

  /**
   * What the buyer had configured when they enquired, for the message the
   * enquiry form sends. Illustrative, like the panel it is read from.
   */
  summary(beds, months, equipment, deposit, monthly) {
    const chip = this.querySelector('[data-model][aria-pressed="true"]');
    const model = chip ? chip.textContent.trim() : 'Reformer';
    const addons = Array.from(this.querySelectorAll('[data-addon]:checked')).map((input) => {
      const label = input.closest('label');
      const name = label ? label.textContent.trim() : input.dataset.addon;
      return input.dataset.addon === 'per-tower' ? `${name} x${this.towerCount}` : name;
    });
    const parts = [
      `${beds} x ${model}`,
      `${months} months`,
      `deposit ${this.money(deposit)}`,
      `equipment ${this.money(equipment)}`,
      `about ${this.money(monthly)} a month`,
    ];
    if (addons.length) parts.push(`add-ons: ${addons.join(', ')}`);
    return `Calculator (illustrative): ${parts.join(', ')}.`;
  }

  render() {
    const beds = Number(this.beds.value);
    const months = Number(this.term.value);
    this.syncTower();
    const equipment = this.equipmentTotal;
    const deposit = this.depositAmount(equipment);

    // Flat-rate model on what is left after the deposit: add the rate across
    // the whole term, then divide.
    const monthly = months > 0 ? ((equipment - deposit) * (1 + this.rate)) / months : 0;
    const perBedPerWeek = beds > 0 ? (monthly * 12) / 52 / beds : 0;

    // Payback: months of class takings to cover everything paid, the deposit
    // plus every monthly payment. Takings are the buyer's class revenue times
    // their classes per week, over an average month.
    const takings = (this.amount(this.revenue) * this.amount(this.sessions) * 52) / 12;
    const paid = deposit + monthly * months;
    const payback = takings > 0 ? Math.max(1, Math.ceil(paid / takings)) : 0;

    this.out.beds.textContent = String(beds);
    this.out.term.textContent = `${months} months`;
    this.out.monthly.textContent = this.money(monthly);
    this.out.total.textContent = this.money(equipment);
    if (this.out.deposit) this.out.deposit.textContent = this.money(deposit);
    if (this.out.payback) {
      this.out.payback.textContent = payback ? `~${payback} ${payback === 1 ? 'month' : 'months'}` : '\u2014';
    }
    const unit = beds === 1 ? 'Reformer' : 'Reformers';
    this.out.breakdown.textContent =
      `${this.money(perBedPerWeek)} per Reformer, per week · ${beds} ${unit} · ${months} months`;
    if (this.enquiry) this.enquiry.dataset.context = this.summary(beds, months, equipment, deposit, monthly);
  }
}

if (!customElements.get('commercial-finance-calculator')) {
  customElements.define('commercial-finance-calculator', CommercialFinanceCalculator);
}
